import React, { useState, useEffect, useCallback } from 'react';
import usePolling from '../../../../hooks/usePolling';
import { useNavigate } from 'react-router-dom';
import {
  FaTelegramPlane,
  FaDatabase,
  FaCode,
  FaClock,
  FaRobot,
  FaChartBar,
  FaArrowRight,
  FaCheckCircle,
  FaPaperPlane,
  FaBolt,
  FaSyncAlt,
} from 'react-icons/fa';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from 'recharts';
import api from '../../../../services/api';
import ActivityFeed from './ActivityFeed';
import { useThemeTokens } from '../../../../utils/themeTokens';
import PageHeader from '../../../common/PageHeader';

const MetricCard = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="bg-surface rounded-lg border border-gray-200 p-4 hover:border-gray-300 transition-colors">
    <div className="flex items-start justify-between mb-3">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</span>
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent || 'bg-gray-100 text-gray-500'}`}
      >
        <Icon className="text-sm" />
      </div>
    </div>
    <div className="text-2xl font-semibold text-gray-900 tracking-tight">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

const StatusDot = ({ status }) => {
  const colors = {
    healthy: 'bg-emerald-500',
    degraded: 'bg-amber-500',
    down: 'bg-red-500',
    unknown: 'bg-gray-400',
  };
  return (
    <span className="relative flex h-2.5 w-2.5">
      {status === 'healthy' && (
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
      )}
      <span
        className={`relative inline-flex rounded-full h-2.5 w-2.5 ${colors[status] || colors.unknown}`}
      />
    </span>
  );
};

const TelegramDashboard = () => {
  const tk = useThemeTokens();
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [recentSchedules, setRecentSchedules] = useState([]);
  const [recentQueries, setRecentQueries] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadDashboard = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [healthRes, dashRes, schedRes, queryRes, activityRes] = await Promise.allSettled([
        api.telegramBI.analytics.getHealth(),
        api.telegramBI.analytics.getDashboard('7d'),
        api.telegramBI.schedules.getAll({
          limit: 8,
          status: 'active',
          sortBy: 'nextRunTime',
          sortOrder: 'asc',
        }),
        api.telegramBI.queries.getAll({ limit: 6, sortBy: 'updatedAt', sortOrder: 'desc' }),
        api.telegramBI.analytics.getActivity({ limit: 20, hours: 24 }),
      ]);

      if (healthRes.status === 'fulfilled') {
        setHealth(healthRes.value.data?.data || healthRes.value.data);
      }
      if (dashRes.status === 'fulfilled') {
        setDashboardData(dashRes.value.data?.data || dashRes.value.data);
      }
      if (schedRes.status === 'fulfilled') {
        const d =
          schedRes.value.data?.data?.schedules ||
          schedRes.value.data?.schedules ||
          schedRes.value.data?.data ||
          [];
        setRecentSchedules(Array.isArray(d) ? d : []);
      }
      if (queryRes.status === 'fulfilled') {
        const d =
          queryRes.value.data?.data?.queries ||
          queryRes.value.data?.queries ||
          queryRes.value.data?.data ||
          [];
        setRecentQueries(Array.isArray(d) ? d : []);
      }
      if (activityRes.status === 'fulfilled') {
        const d =
          activityRes.value.data?.data?.activities || activityRes.value.data?.activities || [];
        setActivities(Array.isArray(d) ? d : []);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Paused while this tab is in the background; refreshes on return.
  usePolling(loadDashboard, 60000);

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '—';
    const diff = new Date(dateStr) - new Date();
    const absDiff = Math.abs(diff);
    const isPast = diff < 0;
    const mins = Math.floor(absDiff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);

    let label;
    if (days > 0) label = `${days}d`;
    else if (hours > 0) label = `${hours}h`;
    else if (mins > 0) label = `${mins}m`;
    else label = 'now';

    return isPast ? `${label} ago` : `in ${label}`;
  };

  const systemStatus = health?.status || 'unknown';

  const quickActions = [
    {
      label: 'New Query',
      icon: FaDatabase,
      path: '/telegram-bi/queries',
      action: 'create',
      color: 'text-emerald-600 bg-emerald-50',
    },
    {
      label: 'New Template',
      icon: FaCode,
      path: '/telegram-bi/templates',
      action: 'create',
      color: 'text-violet-600 bg-violet-50',
    },
    {
      label: 'New Schedule',
      icon: FaClock,
      path: '/telegram-bi/schedules',
      action: 'create',
      color: 'text-amber-600 bg-amber-50',
    },
    {
      label: 'Bot Settings',
      icon: FaRobot,
      path: '/telegram-bi/bot',
      color: 'text-[#0088cc] bg-blue-50',
    },
  ];

  const navSections = [
    {
      label: 'Queries',
      desc: 'SQL query management',
      icon: FaDatabase,
      path: '/telegram-bi/queries',
      count: dashboardData?.queries?.unique_queries,
    },
    {
      label: 'Templates',
      desc: 'Message templates',
      icon: FaCode,
      path: '/telegram-bi/templates',
      count: dashboardData?.templates?.total_templates,
    },
    {
      label: 'Schedules',
      desc: 'Automated delivery',
      icon: FaClock,
      path: '/telegram-bi/schedules',
      count: dashboardData?.schedules?.total_schedules,
    },
    { label: 'Bot', desc: 'Bot configuration', icon: FaRobot, path: '/telegram-bi/bot' },
    {
      label: 'Analytics',
      desc: 'Performance metrics',
      icon: FaChartBar,
      path: '/telegram-bi/analytics',
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-surface border-b border-slate-200">
          <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-3">
            <div className="animate-pulse flex items-center gap-4">
              <div className="hidden sm:block w-11 h-11 bg-gray-200 rounded-xl" />
              <div className="h-6 bg-gray-200 rounded w-48" />
            </div>
          </div>
        </div>
        <div className="p-3 sm:p-6 max-w-[1800px] mx-auto animate-pulse space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-28 bg-gray-200 rounded-lg" />
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaTelegramPlane}
        title="Telegram BI"
        subtitle="Business intelligence & automated reporting"
        actions={
          <>
            <div className="flex items-center gap-2 h-10 px-3 rounded-full border border-gray-200 bg-surface">
              <StatusDot status={systemStatus} />
              <span className="text-xs font-medium text-gray-600 capitalize">{systemStatus}</span>
            </div>
            <button
              type="button"
              onClick={() => loadDashboard(true)}
              aria-label="Refresh"
              title="Refresh"
              className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 border border-slate-300 bg-surface text-slate-700 hover:border-slate-400 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
            >
              <FaSyncAlt
                className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
            </button>
          </>
        }
      />

      <div className="p-3 sm:p-6 max-w-[1800px] mx-auto">
        {/* Metrics Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <MetricCard
            label="Total Executions"
            value={dashboardData?.queries?.total_executions ?? '—'}
            sub={
              dashboardData?.queries?.unique_queries
                ? `${dashboardData.queries.unique_queries} unique queries`
                : null
            }
            icon={FaBolt}
            accent="bg-amber-50 text-amber-600"
          />
          <MetricCard
            label="Success Rate"
            value={
              dashboardData?.queries?.total_executions > 0
                ? `${Math.round((dashboardData.queries.successful_executions / dashboardData.queries.total_executions) * 100)}%`
                : '—'
            }
            sub={
              dashboardData?.queries?.failed_executions
                ? `${dashboardData.queries.failed_executions} failed`
                : null
            }
            icon={FaCheckCircle}
            accent="bg-emerald-50 text-emerald-600"
          />
          <MetricCard
            label="Messages Sent"
            value={dashboardData?.schedules?.messages_sent ?? '—'}
            sub={
              dashboardData?.schedules?.successful_executions
                ? `${dashboardData.schedules.successful_executions} deliveries`
                : null
            }
            icon={FaPaperPlane}
            accent="bg-[#0088cc]/10 text-[#0088cc]"
          />
          <MetricCard
            label="Active Schedules"
            value={dashboardData?.schedules?.total_schedules ?? '—'}
            sub={
              dashboardData?.queries?.avg_execution_time
                ? `Avg ${Math.round(dashboardData.queries.avg_execution_time)}ms`
                : null
            }
            icon={FaClock}
            accent="bg-violet-50 text-violet-600"
          />
        </div>

        {/* Execution Trends Chart */}
        {dashboardData?.trends && dashboardData.trends.length > 1 && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
            <div className="bg-surface rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Execution Trend</h2>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart
                  data={dashboardData.trends.map((t) => ({
                    date: new Date(t.date).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    }),
                    executions: t.executions,
                    successful: t.successful,
                  }))}
                >
                  <defs>
                    <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#45B7AA" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#45B7AA" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={tk.gridSubtle} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: tk.axisLabel }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: tk.axisLabel }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      background: tk.tooltipBg,
                      border: `1px solid ${tk.tooltipBorder}`,
                      color: tk.tooltipText,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="executions"
                    stroke="#45B7AA"
                    fill="url(#trendFill)"
                    strokeWidth={2}
                    name="Executions"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-surface rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Daily Performance</h2>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  data={dashboardData.trends.map((t) => ({
                    date: new Date(t.date).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    }),
                    successful: t.successful,
                    failed: t.executions - t.successful,
                    avgTime: Math.round(t.avg_time || 0),
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={tk.gridSubtle} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: tk.axisLabel }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: tk.axisLabel }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      background: tk.tooltipBg,
                      border: `1px solid ${tk.tooltipBorder}`,
                      color: tk.tooltipText,
                    }}
                  />
                  <Bar
                    dataKey="successful"
                    stackId="a"
                    fill="#10b981"
                    radius={[0, 0, 0, 0]}
                    name="Successful"
                  />
                  <Bar
                    dataKey="failed"
                    stackId="a"
                    fill="#ef4444"
                    radius={[4, 4, 0, 0]}
                    name="Failed"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
          {/* Upcoming Schedules */}
          <div className="xl:col-span-2 bg-surface rounded-lg border border-gray-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">Upcoming Schedules</h2>
              <button
                onClick={() => navigate('/telegram-bi/schedules')}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 transition-colors"
              >
                View all <FaArrowRight className="text-[10px]" />
              </button>
            </div>
            {recentSchedules.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <FaClock className="mx-auto text-2xl text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No active schedules</p>
                <button
                  onClick={() => navigate('/telegram-bi/schedules')}
                  className="mt-2 text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  Create your first schedule
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {recentSchedules.map((sched) => {
                  const isOverdue =
                    sched.next_run_time && new Date(sched.next_run_time) < new Date();
                  return (
                    <div
                      key={sched.schedule_id}
                      className="px-4 py-3 flex items-center gap-4 hover:bg-gray-50/50 transition-colors"
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          sched.schedule_type === 'cron'
                            ? 'bg-violet-50 text-violet-600'
                            : sched.schedule_type === 'recurring'
                              ? 'bg-emerald-50 text-emerald-600'
                              : 'bg-blue-50 text-blue-600'
                        }`}
                      >
                        {sched.schedule_type === 'cron' ? (
                          <FaBolt className="text-xs" />
                        ) : sched.schedule_type === 'recurring' ? (
                          <FaSyncAlt className="text-xs" />
                        ) : (
                          <FaClock className="text-xs" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {sched.schedule_name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {sched.query_name && <span>{sched.query_name}</span>}
                          {sched.query_name && sched.template_name && (
                            <span className="mx-1">→</span>
                          )}
                          {sched.template_name && <span>{sched.template_name}</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div
                          className={`text-xs font-medium ${isOverdue ? 'text-red-600' : 'text-gray-600'}`}
                        >
                          {formatRelativeTime(sched.next_run_time)}
                        </div>
                        <div className="text-[11px] text-gray-400">
                          {sched.schedule_type === 'cron' && sched.cron_expression}
                          {sched.schedule_type === 'recurring' &&
                            `Every ${sched.interval_minutes}m`}
                          {sched.schedule_type === 'once' && 'One-time'}
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium flex-shrink-0 ${
                          isOverdue
                            ? 'bg-red-50 text-red-700'
                            : sched.is_active
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            isOverdue
                              ? 'bg-red-500'
                              : sched.is_active
                                ? 'bg-emerald-500'
                                : 'bg-gray-400'
                          }`}
                        />
                        {isOverdue ? 'Overdue' : sched.is_active ? 'Active' : 'Paused'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-surface rounded-lg border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">Quick Actions</h2>
            </div>
            <div className="p-3 space-y-1.5">
              {quickActions.map((action) => (
                <button
                  key={action.label}
                  onClick={() => navigate(action.path)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${action.color}`}
                  >
                    <action.icon className="text-sm" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors">
                    {action.label}
                  </span>
                  <FaArrowRight className="ml-auto text-[10px] text-gray-300 group-hover:text-gray-500 transition-colors" />
                </button>
              ))}
            </div>
            <div className="px-4 py-3 border-t border-gray-100">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                Modules
              </h3>
              <div className="space-y-1">
                {navSections.map((section) => (
                  <button
                    key={section.label}
                    onClick={() => navigate(section.path)}
                    className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-gray-50 transition-colors text-left group"
                  >
                    <section.icon className="text-xs text-gray-400 group-hover:text-primary-500 transition-colors" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                        {section.label}
                      </span>
                    </div>
                    {section.count !== undefined && (
                      <span className="text-xs text-gray-400 font-medium">{section.count}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Feed + Top Queries */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
          <ActivityFeed activities={activities} loading={loading} />

          {/* Top Queries by Usage */}
          <div className="bg-surface rounded-lg border border-gray-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FaChartBar className="text-primary-500 text-xs" />
                Top Queries
              </h2>
              <button
                onClick={() => navigate('/telegram-bi/queries')}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 transition-colors"
              >
                View all <FaArrowRight className="text-[10px]" />
              </button>
            </div>
            {(!dashboardData?.topQueries || dashboardData.topQueries.length === 0) &&
            recentQueries.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <FaDatabase className="mx-auto text-2xl text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No queries yet</p>
                <button
                  onClick={() => navigate('/telegram-bi/queries')}
                  className="mt-2 text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  Create your first query
                </button>
              </div>
            ) : dashboardData?.topQueries && dashboardData.topQueries.length > 0 ? (
              <div className="divide-y divide-gray-50 max-h-[420px] overflow-y-auto">
                {dashboardData.topQueries.map((q, idx) => (
                  <div
                    key={q.query_id}
                    className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50/50 transition-colors"
                  >
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-gray-500">{idx + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {q.query_name}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-gray-400">{q.execution_count} runs</span>
                        {q.avg_execution_time != null && (
                          <span className="text-[11px] text-gray-400">
                            avg {Math.round(q.avg_execution_time)}ms
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-16 bg-gray-100 rounded-full h-1.5">
                        <div
                          className="bg-primary-500 h-1.5 rounded-full"
                          style={{
                            width: `${Math.min(100, (q.execution_count / (dashboardData.topQueries[0]?.execution_count || 1)) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="divide-y divide-gray-50 max-h-[420px] overflow-y-auto">
                {recentQueries.map((query) => (
                  <div
                    key={query.query_id}
                    className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50/50 transition-colors cursor-pointer"
                    onClick={() => navigate('/telegram-bi/queries')}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600 flex-shrink-0">
                      <FaDatabase className="text-xs" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {query.query_name}
                      </div>
                      {query.query_description && (
                        <div className="text-[11px] text-gray-400 truncate">
                          {query.query_description}
                        </div>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                        query.is_public ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {query.is_public ? 'Public' : 'Private'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelegramDashboard;
